import { useEffect, useRef, useCallback } from 'react';
import anamConfig from '../config/anamConfig';

function useAnamVideoStreamRefresh({
  onRefresh,
  aiResponsePlaybackActiveRef,
  isAvatarTalking,
  isActive
}) {
  const intervalMs = Math.floor(
    anamConfig.videoStreamTimeoutMs * anamConfig.videoStreamRefreshRatio
  );

  const pendingRefreshRef = useRef(false);
  const intervalIdRef = useRef(null);
  const isAvatarTalkingRef = useRef(isAvatarTalking);
  // Stable ref to onRefresh so interval callback never closes over a stale fn.
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    isAvatarTalkingRef.current = isAvatarTalking;
  }, [isAvatarTalking]);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const isPlaybackIdle = useCallback(() => {
    return !aiResponsePlaybackActiveRef.current && !isAvatarTalkingRef.current;
  }, [aiResponsePlaybackActiveRef]);

  const scheduleRefresh = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    intervalIdRef.current = setInterval(() => {
      if (isPlaybackIdle()) {
        pendingRefreshRef.current = false;
        console.log('[Anam] Playback idle — video stream refresh');
        onRefreshRef.current();
      } else {
        pendingRefreshRef.current = true;
        console.log('[Anam] Playback active — video stream refresh deferred');
      }
    }, intervalMs);
  }, [intervalMs, isPlaybackIdle]); // Restored original dependency

  // Start/stop the interval when stream liveness changes.
  useEffect(() => {
    if (!isActive) {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      pendingRefreshRef.current = false;
      return;
    }

    scheduleRefresh();

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [isActive, scheduleRefresh]);

  // Execute any pending refresh the moment the avatar goes silent.
  // NOTE: Do NOT check aiResponsePlaybackActiveRef.current here — it is reset
  // by AnamVideoStream's own useEffect (declared after this hook), which runs
  // after this effect due to React's hook-declaration order. Checking the ref
  // here always sees the stale "true" value and causes a false early-return.
  // isAvatarTalking === false is the correct and sufficient idle signal.
  useEffect(() => {
    if (!pendingRefreshRef.current) return;
    if (!isActive) return;
    if (isAvatarTalking) return;

    pendingRefreshRef.current = false;
    console.log(
      '[Anam] Playback ended — running deferred video stream refresh'
    );
    onRefreshRef.current();

    scheduleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvatarTalking, isActive]);
}

export default useAnamVideoStreamRefresh;
